// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useReactFlow, useUpdateNodeInternals } from '@xyflow/react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import {
  deleteEquipmentGroup,
  updateEquipmentGroup,
  updateEquipmentGroupItem,
  moveNodeBetweenGroups,
  lockEquipmentGroup,
  unlockEquipmentGroup,
  rejectLockRequest,
  unlockAllEquipmentGroupLocks,
} from '../../store/equipment/equipmentActions';

import { isUserAdmin, pendingLockRequest, userPermission } from '../../store/user/userSelectors';
import {
  selectFacilityEquipmentGroup,
  selectEquipmentGroups,
  selectAllEquipmentInsideGroups,
} from '../../store/equipment/equipmentSelectors';
import { selectStudioEquipmentOptions } from '../../store/studio/studioSelector';

import { toggleNode, relayoutGroup, getAncestorChain } from '../../store/equipment/equipmentUtils';
import {
  setGroupOpen,
  setGroupLayout,
  setGroupsOpen,
} from '../../store/userPreferences/userPreferencesSlice';
import { NODES_BASE_PATH } from '../../routes/Routes/NodesRoutes/nodesBasePath';

export const useNodeActions = ({ node, onClose, showDeleteConfirmation } = {}) => {
  const { setNodes, getNode, getNodes, getNodesBounds, fitView } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const navigate = useNavigate();
  const facilityGroup = useSelector(selectFacilityEquipmentGroup, shallowEqual);
  const equipmentGroups = useSelector(selectEquipmentGroups, shallowEqual);
  const allEquipment = useSelector(selectAllEquipmentInsideGroups, shallowEqual);
  const studioEquipmentOptions = useSelector(selectStudioEquipmentOptions, shallowEqual);
  const pendingRequest = useSelector(pendingLockRequest, shallowEqual);
  const isAdmin = useSelector(isUserAdmin, shallowEqual);
  const permission = useSelector(userPermission, shallowEqual);

  const dispatch = useDispatch();

  // Equipment groups id if node is a equipment group
  const groupId = node?.id ?? node?.equip_group_uuid;

  /* ================= Equipment group actions ================= */
  // Toggle a node open/closed. Pass { id, isOpen, nodeToOpen } to target a specific node and
  // optionally navigate to its detail page. When called with no args, toggles the bound node
  // without navigating (used by keyboard shortcut and context menu).
  const handleToggle = useCallback(
    ({ id: targetId, isOpen: targetIsOpen, nodeToOpen = null } = {}) => {
      const resolvedId = targetId ?? node?.id;
      const resolvedIsOpen = targetIsOpen ?? node?.data?.isOpen ?? false;

      if (!resolvedId) {
        return;
      }

      const target = getNode(resolvedId);
      if (!target) {
        return;
      }

      if (
        target.type === 'equipmentGroup' &&
        (target.data?.state?.equipment?.length || target.data?.state?.children?.length)
      ) {
        if (!target.data) {
          return;
        }
        const newIsOpen = !target.data.isOpen;

        setNodes(nds => toggleNode(nds, resolvedId));

        dispatch(setGroupOpen({ nodeId: resolvedId, isOpen: newIsOpen }));
        if (newIsOpen) {
          // toggleNode also opens any closed ancestors — sync those too
          const allNodes = getNodes();
          const openUpdates = {};
          getAncestorChain(resolvedId, allNodes).forEach(ancestorId => {
            const ancestor = allNodes.find(n => n.id === ancestorId);
            if (ancestor && !ancestor.data?.isOpen) {
              openUpdates[ancestorId] = true;
            }
          });
          if (Object.keys(openUpdates).length) {
            dispatch(setGroupsOpen(openUpdates));
          }
        }
        requestAnimationFrame(() => {
          // Recurse — toggleNode also re-opens any previously-open nested subgroups
          const allNodes = getNodes();
          const descendantIds = [];
          const collectDescendants = parentId => {
            allNodes.forEach(n => {
              if (n.parentId === parentId) {
                descendantIds.push(n.id);
                collectDescendants(n.id);
              }
            });
          };
          collectDescendants(resolvedId);
          updateNodeInternals([resolvedId, ...descendantIds]);
        });
      }

      if (resolvedIsOpen || !nodeToOpen) {
        return;
      }

      const isEquipment = Boolean(nodeToOpen?.co_equip_uuid);
      const type = isEquipment ? 'equipment' : 'equipmentGroup';
      const navTargetId = isEquipment ? nodeToOpen.co_equip_uuid : nodeToOpen.equip_group_uuid;
      navigate(`${NODES_BASE_PATH}/${type}/${navTargetId}`, { replace: true });
    },
    [node, getNode, getNodes, setNodes, updateNodeInternals, navigate, dispatch]
  );

  // Toggle a node open/closed and all its nested children equipment groups open.
  const handleExpandAll = useCallback(() => {
    if (!node) {
      return;
    }
    const currentNodes = getNodes();
    const nonDirect = node.data.state?.equipment?.filter(e => !e.direct_member) ?? [];
    const subgroupIds = [
      ...new Set(
        nonDirect
          .map(e => currentNodes.find(n => n.id === e.co_equip_uuid)?.parentId)
          .filter(Boolean)
      ),
    ];

    setNodes(nds => {
      let updated = toggleNode(nds, node.id, true);
      subgroupIds.forEach(id => {
        updated = toggleNode(updated, id, true);
      });
      return updated;
    });

    // Persist open state for all expanded groups
    const openUpdates = { [node.id]: true };
    subgroupIds.forEach(id => {
      openUpdates[id] = true;
    });
    dispatch(setGroupsOpen(openUpdates));
  }, [node, setNodes, getNodes, dispatch]);

  /* ================= Admin actions (both group and equipment) ================= */
  const handleDelete = useCallback(
    async item => {
      if (!item) {
        return;
      }

      if (item.type === 'equipmentGroup') {
        await dispatch(deleteEquipmentGroup({ equipGroupUUID: item.id ?? item.equip_group_uuid }));
      } else if (item.type === 'equipment') {
        const parentId = item.parentId ?? facilityGroup?.equip_group_uuid;
        await dispatch(
          updateEquipmentGroupItem({ equip_group_uuid: parentId, remove_equipment: [item.id] })
        );
      }
      onClose?.();
    },
    [dispatch, facilityGroup, onClose]
  );

  const confirmAndDelete = useCallback(
    async item => {
      const confirmed = await showDeleteConfirmation({ nodes: [item], edges: [] });
      if (confirmed) {
        await handleDelete(item);
      }
      return confirmed;
    },
    [showDeleteConfirmation, handleDelete]
  );

  const handleConfirmationDelete = useCallback(
    async ({ nodes: nodesToCheck, edges }) => {
      if (nodesToCheck?.length > 0) {
        if (!isAdmin) {
          return false;
        }
        const nodeToDelete = nodesToCheck.find(n => n.id === node?.id);
        if (nodeToDelete?.type === 'facility') {
          return false;
        }
        return await confirmAndDelete(nodeToDelete);
      }
      if (edges?.length > 0) {
        return permission !== 'operator';
      }
      return false;
    },
    [node, isAdmin, permission, confirmAndDelete]
  );

  // Admin only: change group type
  const handleChangeGroupType = useCallback(
    async typeUuid => {
      if (!node) {
        return;
      }

      const groupData = node.data?.state ?? node;
      await dispatch(updateEquipmentGroup({ ...groupData, equip_group_type_uuid: typeUuid }));
    },
    [node, dispatch]
  );

  // Admin only: move node back to the facility (root) group
  const handleRemoveFromGroup = useCallback(async () => {
    if (!node || !facilityGroup) {
      return;
    }
    await dispatch(
      moveNodeBetweenGroups({
        nodeId: node.id,
        fromGroupId: node.parentId,
        toGroupId: facilityGroup.equip_group_uuid,
        type: node.type,
      })
    );
  }, [node, facilityGroup, dispatch]);

  /* ================= Camera control actions ================= */
  // Camera control group: navigate to the camera control page, which acquires the lock itself
  // (via cameraControlLoader) if the group is free, or takes it over if it's held by someone
  // else and the user is admin
  const handleCameraControl = useCallback(() => {
    if (!node) {
      return;
    }
    navigate(`${NODES_BASE_PATH}/${groupId}/cameracontrol`);
  }, [node, groupId, navigate]);

  // Non-admin: request the lock, or cancel an existing pending request
  const handleRequestControl = useCallback(async () => {
    if (!node) {
      return;
    }
    if (pendingRequest) {
      await dispatch(rejectLockRequest({ lock_req_uuid: pendingRequest.lock_req_uuid }));
    } else {
      await dispatch(lockEquipmentGroup({ equip_group_uuid: groupId }));
    }
  }, [node, groupId, pendingRequest, dispatch]);

  // Unlock the group when the current user holds the lock
  const handleUnlock = useCallback(async () => {
    if (!node) {
      return;
    }
    await dispatch(unlockEquipmentGroup({ equip_group_uuid: groupId }));
  }, [node, groupId, dispatch]);

  // Admin only: unlock all locks (optionally discarding pending requests) within the bound equipment group
  const handleUnlockAll = useCallback(
    async ({ discardPendingRequests } = {}) => {
      if (!node) {
        return;
      }
      await dispatch(
        unlockAllEquipmentGroupLocks({
          equip_group_uuid: groupId,
          discard_pending_requests: discardPendingRequests,
        })
      );
    },
    [node, groupId, dispatch]
  );

  const handleLayoutChange = useCallback(
    layout => {
      if (!node) {
        return;
      }
      dispatch(setGroupLayout({ nodeId: node.id, layout }));
      setNodes(nds => {
        const updated = nds.map(n =>
          n.id === node.id ? { ...n, data: { ...n.data, layout } } : n
        );
        return relayoutGroup(updated, node.id);
      });
      onClose?.();
    },
    [node, setNodes, onClose, dispatch]
  );

  /* ================= Group membership  ================= */
  // Highlight nodes selected in the "available" list and fit the view around them (plus the
  // bound group), previewing where they currently live before they're moved.
  const highlightAvailableSelection = useCallback(
    selection => {
      if (!selection?.length) {
        setNodes(nds =>
          nds.map(n =>
            n.data?.isHighlighted ? { ...n, data: { ...n.data, isHighlighted: false } } : n
          )
        );
        return;
      }

      const currentNodes = getNodes();
      const nodeIds = new Set(currentNodes.map(n => n.id));
      const highlightable = selection.filter(id => nodeIds.has(id));

      setNodes(nds =>
        nds.map(n =>
          highlightable.includes(n.id)
            ? { ...n, data: { ...n.data, isHighlighted: true } }
            : { ...n, data: { ...n.data, isHighlighted: false } }
        )
      );

      requestAnimationFrame(() => {
        highlightable.forEach(nodeId => {
          const nodesToOpen = getAncestorChain(nodeId, currentNodes);
          nodesToOpen.forEach(id => {
            setNodes(nds => toggleNode(nds, id, true));
          });
        });

        const fitIds = [...new Set([...highlightable, node?.id])]
          .filter(Boolean)
          .map(id => ({ id }));
        const nodesList = getNodes().filter(n => fitIds.some(f => f.id === n.id));
        const bounds = getNodesBounds(nodesList);

        const container = document.querySelector('.react-flow');
        if (container && bounds.width && bounds.height) {
          const { clientWidth: w, clientHeight: h } = container;
          const requiredZoom = Math.min(w / bounds.width, h / bounds.height);
          fitView({
            nodes: fitIds,
            padding: 0.2,
            duration: 500,
            minZoom: Math.max(requiredZoom * 0.9, 0.02),
            maxZoom: 2,
          });
        } else {
          fitView({ nodes: fitIds, padding: 0.2, duration: 500 });
        }
      });
    },
    [node, setNodes, getNodes, getNodesBounds, fitView]
  );

  // Resolve the group a node currently belongs to, so it can be moved out before joining the
  // bound group.
  const getFromGroupId = useCallback(
    (nodeId, isEquipment) => {
      if (isEquipment) {
        const group = equipmentGroups.find(g =>
          g.equipment?.some(e => e.co_equip_uuid === nodeId && e.direct_member === true)
        );
        return group?.equip_group_uuid;
      }

      const group = equipmentGroups.find(g => g.equip_group_uuid === nodeId);
      return group?.parent_uuid;
    },
    [equipmentGroups]
  );

  // Move equipment/groups added or removed from the bound group's DualListBox selection.
  // Removed items go back to the facility (root) group.
  const updateGroupMembership = useCallback(
    async (added, removed) => {
      if (!node?.id || (!added.length && !removed.length)) {
        return;
      }

      setNodes(nds =>
        nds.map(n =>
          n.data?.isHighlighted ? { ...n, data: { ...n.data, isHighlighted: false } } : n
        )
      );

      const equipmentMap = new Map(studioEquipmentOptions.map(opt => [opt.value, opt]));
      const facilityEquipmentIds = allEquipment?.map(e => e.co_equip_uuid) ?? [];

      for (const nodeId of added) {
        const isEquipment = equipmentMap.has(nodeId);
        const fromGroupId = getFromGroupId(nodeId, isEquipment);
        const checkEquipmentUsed = isEquipment && !facilityEquipmentIds.includes(nodeId);

        await dispatch(
          moveNodeBetweenGroups({
            nodeId,
            fromGroupId,
            toGroupId: node.id,
            type: isEquipment ? 'equipment' : 'equipmentGroup',
            checkEquipmentUsed,
          })
        );

        requestAnimationFrame(() => {
          const updatedNodes = getNodes();
          const nodesToOpen = getAncestorChain(nodeId, updatedNodes);
          nodesToOpen.forEach(id => {
            setNodes(nds => toggleNode(nds, id, true));
          });
          setNodes(nds => toggleNode(nds, node.id, true));
        });
      }

      for (const nodeId of removed) {
        const isEquipment = equipmentMap.has(nodeId);
        await dispatch(
          moveNodeBetweenGroups({
            nodeId,
            fromGroupId: node.id,
            toGroupId: facilityGroup?.equip_group_uuid,
            type: isEquipment ? 'equipment' : 'equipmentGroup',
            checkEquipmentUsed: false,
          })
        );
      }
    },
    [
      node,
      dispatch,
      setNodes,
      getNodes,
      getFromGroupId,
      studioEquipmentOptions,
      allEquipment,
      facilityGroup,
    ]
  );

  // Return all the action handlers for use in the context menu or other components
  return {
    handleToggle,
    handleExpandAll,
    handleLayoutChange,
    confirmAndDelete,
    handleConfirmationDelete,
    handleChangeGroupType,
    handleRemoveFromGroup,
    handleCameraControl,
    handleRequestControl,
    handleUnlock,
    handleUnlockAll,
    highlightAvailableSelection,
    updateGroupMembership,
  };
};
